import axios from 'axios';

// Base API URL
const API_URL = 'https://csypblogbackend.azurewebsites.net/api';

// Axios instance for reusable configuration
const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 5000, // Set a timeout of 5 seconds
});

// Add an interceptor to include the Authorization token automatically
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('jwtToken'); // Assuming token is stored in localStorage
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('Authorization header added:', config.headers.Authorization);
        } else {
            console.warn('No JWT token found in localStorage');
        }
        return config;
    },
    (error) => Promise.reject(error) // Handle request errors
);

// Handle API response and errors centrally
const handleApiResponse = (response) => {
    if (response.status >= 200 && response.status < 300) {
        return response.data;
    } else {
        console.error(`Unexpected status code: ${response.status}`);
        throw new Error(`Unexpected status code: ${response.status}`);
    }
};

const handleApiError = (err) => {
    if (err.response) {
        console.error('Server error:', err.response.data);
        throw new Error(err.response.data.message || 'Server error occurred');
    } else if (err.request) {
        console.error('No response received:', err.request);
        throw new Error('No response from the server');
    } else {
        console.error('Error message:', err.message);
        throw new Error(err.message);
    }
};

// Login and save the token
export const login = async (username, password) => {
    try {
        const response = await apiClient.post('/login', { username, password });
        const token = response.data.token; // Assuming the token is returned as `token`
        localStorage.setItem('jwtToken', token); // Save token in localStorage
        console.log('Login successful, token saved:', token);
        return token;
    } catch (err) {
        console.error('Error during login:', err.response?.data || err.message);
        throw err;
    }
};

// Logout and clear the token
export const logout = () => {
    localStorage.removeItem('jwtToken'); // Remove token on logout
    console.log('User logged out, token removed.');
};

// Fetch all essays
export const fetchEssays = async () => {
    try {
        const response = await apiClient.get('/essays');
        return handleApiResponse(response);
    } catch (err) {
        return handleApiError(err);
    }
};

// Add a new essay
export const addEssay = async (title, content) => {
    try {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            throw new Error('No JWT token found. Please log in.');
        }
        console.log('Using token for Authorization:', token);

        const response = await apiClient.post('/essays', { title, content });
        return handleApiResponse(response);
    } catch (err) {
        if (err.response?.status === 401) {
            console.error('Unauthorized: Please log in again.');
        } else if (err.response?.status === 403) {
            console.error('Forbidden: You do not have permission to perform this action.');
        } else {
            console.error('Error adding essay:', err.response?.data || err.message);
        }
        throw err;
    }
};

// Fetch a single essay by ID
export const fetchEssayById = async (id) => {
    try {
        const response = await apiClient.get(`/essays/${id}`);
        return handleApiResponse(response);
    } catch (err) {
        return handleApiError(err);
    }
};

// Delete an essay by ID
export const deleteEssay = async (id) => {
    try {
        const response = await apiClient.delete(`/essays/${id}`);
        return handleApiResponse(response);
    } catch (err) {
        return handleApiError(err);
    }
};
